import type { DocumentType } from '../../types/tuition-assistance.ts';
import type { TASProgramTemplateModelFieldsSignature } from './tas-program-template.type.ts';
import type { TasProgramTemplate } from './tas-program-template.type.ts';
import { FIELD_KEY_BY_CLASSIFICATION } from './tas-program-template.constants.ts';

// TODO: the usage of this interface is not consistent with the usage of the interface in approver
// overview.
export interface TasProgramTemplateExtension extends TasProgramTemplate {}
export class TasProgramTemplateExtension {
  static FIELD_KEY_BY_CLASSIFICATION: Record<string, string> = {
    PROGRAM_APPROVAL_DOCUMENTS: 'PROGRAM_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD',
    COURSE_APPROVAL_DOCUMENTS: 'COURSE_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD',
    COMPLETION_DOCUMENTS: 'COMPLETION_DOCUMENTS_ATTACHMENT_FIELD',
    PROGRAM_COMPLETION_DOCUMENTS: 'PROGRAM_COMPLETION_DOCUMENTS_ATTACHMENT_FIELD',
  };

  // ── Computed Properties ──
  // Ember Data doesn't support "multiple foreign keys".
  // For more information about why these are setup as cached getters, see:
  // https://github.com/emberjs/data/issues/8392
  get programApproveConditions() {
    return Object.freeze(
      this.conditions.filter((condition) => condition.province === 'program_approve_workflow')
    );
  }

  get programApproveWithdrawCompleteConditions() {
    return Object.freeze(
      this.conditions.filter(
        (condition) => condition.province === 'program_approve_withdraw_complete_workflow'
      )
    );
  }

  get applicationApproveCoursesConditions() {
    return Object.freeze(
      this.conditions.filter(
        (condition) => condition.province === 'application_approve_courses_workflow'
      )
    );
  }

  get applicationApproveEvidenceConditions() {
    return Object.freeze(
      this.conditions.filter(
        (condition) => condition.province === 'application_approve_evidence_workflow'
      )
    );
  }

  get typeTwoApplicationConditions() {
    if (
      this.typeClassification === 'TAS.ProgramType.2' ||
      this.typeClassification === 'TAS.ProgramType.4.2'
    ) {
      return Object.freeze([
        ...this.applicationApproveCoursesConditions,
        ...this.applicationApproveEvidenceConditions,
      ]);
    } else return this.applicationApproveCoursesConditions;
  }

  get fieldEntries() {
    const fieldsHash = this.fields || {};
    return Object.entries(fieldsHash);
  }

  /**
   * Returns the distinct AI-classifiable document types required at the given
   * approval-step classification, derived from the per-label
   * `value_document_types` map on the corresponding `*_ATTACHMENT_FIELD`.
   *
   * Returns `[]` for unknown classifications, for legacy templates with no map,
   * and when every entry maps to `'general'`. Callers (e.g. the categorized
   * upload component) treat an empty array as the signal to fall back to the
   * legacy free-form upload flow.
   */
  requiredDocumentTypesFor(classification: string | null | undefined): DocumentType[] {
    if (!classification) return [];
    const fieldKey = FIELD_KEY_BY_CLASSIFICATION[classification];
    if (!fieldKey) return [];
    const field = this.fields?.[fieldKey as keyof TASProgramTemplateModelFieldsSignature] as
      { value_document_types?: Record<string, DocumentType> } | undefined;
    const map = field?.value_document_types ?? {};
    const distinct = new Set<DocumentType>();
    for (const type of Object.values(map)) {
      if (type && type !== 'general') distinct.add(type);
    }
    return [...distinct];
  }

  /**
   * Returns all admin-configured document entries (label + type) for the given
   * approval-step classification, preserving insertion order and including
   * `'general'` entries. Callers that need to render one row per configured
   * attachment (with the admin's custom label) should use this instead of
   * `requiredDocumentTypesFor`.
   */
  documentEntriesFor(
    classification: string | null | undefined
  ): Array<{ label: string; type: DocumentType }> {
    if (!classification) return [];
    const fieldKey = FIELD_KEY_BY_CLASSIFICATION[classification];
    if (!fieldKey) return [];
    const field = this.fields?.[fieldKey as keyof TASProgramTemplateModelFieldsSignature] as
      { values?: string[]; value_document_types?: Record<string, DocumentType> } | undefined;
    const map = field?.value_document_types ?? {};
    const labels = field?.values ?? [];
    const entries = labels
      .filter((label) => !!label)
      .map((label) => ({ label, type: map[label] ?? 'general' }));
    const mappedLabels = new Set(labels);
    const orphanedEntries = Object.entries(map)
      .filter(([label, type]) => !!label && !!type && !mappedLabels.has(label))
      .map(([label, type]) => ({ label, type }));

    return [...entries, ...orphanedEntries];
  }

  // NOTE: Instance fields are set on the tas-program-instance model as they tie directly into info from the employee model
  // Returns an object to be used for the initial values for a new TasApplication's fields object
  get applicationFieldsHash() {
    const applicationEntries = this.fieldEntries
      .filter((entry) => {
        return entry[1]?.province === 'APPLICATION';
      })
      .map((entry) => {
        return [entry[0], { values: entry[1]?.values || [] }];
      });
    return Object.fromEntries(applicationEntries);
  }

  get courseFieldsHash() {
    const excludedFields = new Set([
      'COURSE_APPROVAL_DOCUMENTS_STRING',
      'COMPLETION_DOCUMENTS_ATTACHMENT_FIELD',
      'COURSE_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD',
      'COMPLETION_DOCUMENTS_STRING',
      'COURSE_INFORMATION',
      'APPROVED_COURSE_TOTAL',
    ]);

    const courseApplicantEntries = this.fieldEntries
      .filter((entry) => {
        return entry[1]?.province === 'COURSE' && !excludedFields.has(entry[0]);
      })
      .map((entry) => {
        return [entry[0], { values: entry[1]?.values || [] }];
      });

    return Object.fromEntries(courseApplicantEntries);
  }

  get instanceCustomFields() {
    const fields = this.customFields || [];
    return fields.filter((field) => ['PROGRAM', 'EMPLOYEE'].includes(field.province));
  }

  get applicationCustomFields() {
    const fields = this.customFields || [];
    return fields.filter((field) => field.province === 'APPLICATION');
  }

  get courseCustomFields() {
    const fields = this.customFields || [];
    return fields.filter((field) => field.province === 'COURSE');
  }

  get programName() {
    return this.fields?.['PROGRAM_NAME']?.values?.[0] || '';
  }

  get programDescription() {
    return this.fields?.['PROGRAM_DESCRIPTION']?.values?.[0] || '';
  }

  get programApproverInstructions() {
    const instructions = this.approverInstructions || [];
    return instructions.sort((a, b) => a.ordinal - b.ordinal);
  }

  get programFaqs() {
    const faqs = this.faqs || [];
    // `faqs` is an unformatted attribute, so each entry reaches us in whatever
    // shape its author's tool wrote: the legacy admin wrote heading/body, the
    // program builder writes question/answer. Emit both spellings from here so
    // a consumer reading either pair gets real text rather than undefined.
    //
    // A single entry can carry BOTH pairs: the program builder spreads the
    // existing entry when it edits, so a legacy FAQ edited there keeps its
    // heading/body and gains question/answer, and its "add" seeds question and
    // answer as empty strings. So blank has to fall through to the other
    // spelling — `??` would stop at '' and render the empty row this is meant
    // to fix. Where both are non-blank the builder pair wins, it being the
    // newer writer.
    //
    // `ordinal` is optional upstream — absent ordinals sort as 0, and mapping
    // first means the sort no longer reorders the record's own array in place.
    const firstNonBlank = (...values: (string | undefined)[]) =>
      values.find((value) => typeof value === 'string' && value.trim() !== '') ?? '';

    return faqs
      .map((faq) => {
        const question = firstNonBlank(faq.question, faq.heading);
        const answer = firstNonBlank(faq.answer, faq.body);
        return {
          ...faq,
          ordinal: faq.ordinal ?? 0,
          question,
          heading: question,
          answer,
          body: answer,
        };
      })
      .sort((a, b) => a.ordinal - b.ordinal);
  }

  get programPaymentType() {
    return this.fields?.['PROGRAM_PAYMENT']?.values?.[0] || '';
  }

  get calculateTowardsLimitBasedOn() {
    return this.fields?.['CALCULATE_TOWARDS_TOTAL_BASED_ON']?.values?.[0] || '';
  }

  get isPrepaidProgram() {
    return this.programPaymentType === 'ON_ATTEND';
  }

  get isReimbursementProgram() {
    return this.programPaymentType === 'ON_FULFILLED';
  }

  get isDirectPayProgram() {
    return (
      this.fields?.['PAYMENT_TYPE_FOR_DIRECT_PAY']?.values?.[0] ===
      'TAS.AssistanceManner.COURSE_CASH'
    );
  }

  get isPreApprovalEnabled() {
    return !!this.fields?.['PROGRAM_APPROVE_WORKFLOW']?.required;
  }

  get isCoursePreApprovalEnabled() {
    return !!this.fields?.['APPLICATION_APPROVE_COURSES_WORKFLOW']?.required;
  }

  get isMultipleInstanceProgram() {
    return this.fields?.['MULTIPLE_INSTANCE_PROGRAM']?.values?.[0] || false;
  }

  get isTrackingOnlyProgram() {
    return (
      this.typeClassification === 'TAS.ProgramType.1' ||
      Boolean(this.fields?.TRACKING_ONLY_PROGRAM?.values?.[0])
    );
  }

  get isCertificateProgram() {
    return this.fields?.['CERTIFICATE_OTHER_PROGRAM']?.values?.[0] || false;
  }

  get annualLimitsByEmploymentType() {
    return this.fields?.['MAXIMUM_ANNUAL_REIMBURSEMENT_BY_EMPLOYMENT_TYPE']?.values?.[0] || {};
  }

  get annualLimitForAllEmploymentTypes() {
    return this.fields?.['MAXIMUM_ANNUAL_REIMBURSEMENT']?.values?.[0];
  }

  get calculateTotalByCredit() {
    return this.fields?.['PRE_CALC_REQUESTED_TOTAL_BY_CREDIT']?.values?.[0] || false;
  }

  get ratePerCredit() {
    return this.fields?.['REIMBURSEMENT_PER_COURSE_CREDIT']?.values?.[0];
  }

  get calculateTotalByPercent() {
    return this.fields?.['PRE_CALC_REQUESTED_TOTAL_BY_PERCENT']?.values?.[0] || false;
  }

  get reimbursementPercentage() {
    return this.fields?.['REIMBURSEMENT_PERCENTAGE']?.values?.[0] || 100;
  }

  get calculateTotalByClaimsFinancials() {
    return this.fields?.['CALCULATE_REIMBURSEMENT_BY_CLAIMS_FINANCIALS']?.values?.[0] || false;
  }

  get useBalanceMultiplierForProgramLimits() {
    return this.fields?.['USE_BALANCE_MULTIPLIER_FOR_PROGRAM_LIMITS']?.values?.[0] || false;
  }

  get calculateTotalByGPA() {
    return this.fields?.['PRE_CALC_REQUESTED_TOTAL_BY_GPA']?.values?.[0] || false;
  }

  get passingGrades() {
    return this.fields?.['PASSING_ACADEMIC_PERFORMANCES']?.values || [];
  }

  get hideProcessDescriptionSteps() {
    return this.fields?.['REMOVE_PROCESS_DESCRIPTION_STEPS']?.values[0] || false;
  }

  // TODO: Refactor this or consuming code because returning two types is confusing.
  get annualLimitValue() {
    const checkEmploymentTypesForLength =
      Object.keys(this.annualLimitsByEmploymentType).length === 0;
    if (checkEmploymentTypesForLength) {
      return this.annualLimitForAllEmploymentTypes;
    } else {
      return this.annualLimitsByEmploymentType;
    }
  }

  get lifetimeLimitForAllEmploymentTypes() {
    const allValue = this.fields?.['MAXIMUM_LIFETIME_REIMBURSEMENT']?.values?.[0];

    return allValue ? { all: allValue } : {};
  }

  get lifetimeLimitsByEmploymentType() {
    return this.fields?.['MAXIMUM_LIFETIME_REIMBURSEMENT_BY_EMPLOYMENT_TYPE']?.values?.[0] || {};
  }

  get lifetimeLimitValue() {
    return this.lifetimeLimitsByEmploymentType || this.lifetimeLimitForAllEmploymentTypes;
  }

  get allowPreApprovalSubmissionBeyondLimit() {
    return this.fields?.['ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT']?.values?.[0] || false;
  }

  get requireAttachmentsForCourseApproval() {
    return !!this.fields?.['COURSE_APPROVAL_DOCUMENTS_STRING']?.required;
  }

  get attachmentsForCourseApprovalVisible() {
    return !!this.fields?.['COURSE_APPROVAL_DOCUMENTS_STRING']?.visible;
  }

  get requireAttachmentsForCourseCompletion() {
    return !!this.fields?.['COMPLETION_DOCUMENTS_STRING']?.required;
  }

  get attachmentsForCourseCompletionVisible() {
    return !!this.fields?.['COMPLETION_DOCUMENTS_STRING']?.visible;
  }

  get requireAttachmentsForProgramApproval() {
    return !!this.fields?.['PROGRAM_APPROVAL_DOCUMENTS_STRING']?.required;
  }

  get attachmentsForProgramApprovalVisible() {
    return !!this.fields?.['PROGRAM_APPROVAL_DOCUMENTS_STRING']?.visible;
  }

  get programApprovalRequired() {
    return !!this.fields?.['PROGRAM_APPROVE_WORKFLOW']?.required;
  }

  get courseApprovalRequired() {
    return !!this.fields?.['APPLICATION_APPROVE_COURSES_WORKFLOW']?.required;
  }

  get evidenceApprovalRequired() {
    return (
      !!this.fields?.['APPLICATION_APPROVE_EVIDENCE_WORKFLOW']?.required &&
      !this.fields?.['WAIVE_EVIDENCE_APPROVAL_REQUIREMENT']?.required
    );
  }

  get courseGradeRequired() {
    return !!this.fields?.['COURSE_GRADE']?.required;
  }

  get semesterCodeIsVisible() {
    return !!this.fields?.['SEMESTER_CODE']?.visible || false;
  }

  get scholarshipsAreVisible() {
    return !!this.fields?.['SCHOLARSHIPS_RECEIVED']?.visible;
  }

  get courseReceiptDateVisible() {
    return !!this.fields?.['COURSES_RECEIPT_DATE']?.visible;
  }

  get isLaunched() {
    return this.state === 'TAS.ProgramTemplateState.LAUNCHED';
  }

  get isDraft() {
    return this.state === 'TAS.ProgramTemplateState.DRAFT';
  }

  get isSuspended() {
    return this.state === 'TAS.ProgramTemplateState.SUSPENDED';
  }

  get suspendedProgramDisclosure() {
    return this.fields?.['SUSPENDED_PROGRAM_DISCLOSURE']?.values?.[0] || '';
  }

  get fractionalBenefitPercentForClaimsFinancials(): number {
    const rawPercentage =
      (this.fields?.['REIMBURSEMENT_PERCENTAGE_FOR_CLAIMS_FINANCIALS']?.values?.[0] || 100) / 100;
    return parseFloat(rawPercentage.toFixed(2));
  }

  get hasInProgressProgramInstances() {
    // returns instances that have been submitted, but not in terminal state
    return this.tasProgramInstances.some((instance) => instance.programInstanceInProgress);
  }

  get eligibilityWaitingPeriod() {
    return this.fields?.['ELIGIBILITY_WAITING_PERIOD']?.values?.[0] || 0;
  }

  get isDependentProgram() {
    return this.fields?.['DEPENDENT_PROGRAM']?.values?.[0] || false;
  }

  get isVoucherProgram() {
    return this.fields?.['VOUCHER_PROGRAM']?.values?.[0] || false;
  }

  get lastDayToSubmitApplicationForPreApproval() {
    // number of days AFTER course start that you can apply
    return this.fields?.['REGISTRATION_TO_PRE_APPROVAL_MAX_LAG_TIME']?.values?.[0] || '';
  }

  get coursesPreApprovalSubmissionMaxLeadTime() {
    // MAX number of days BEFORE course start that you can apply
    return this.fields?.['PRE_APPROVAL_TO_REGISTRATION_MAX_LEAD_TIME']?.values?.[0] || '';
  }

  get coursesPreApprovalSubmissionMinLeadTime() {
    // MIN number of days BEFORE course start that you can apply
    return this.fields?.['PRE_APPROVAL_TO_REGISTRATION_MIN_LEAD_TIME']?.values?.[0] || '';
  }

  get typeClassification() {
    return this.fields?.['TYPE']?.values?.[0] || '';
  }

  get lockEmployeeStatusBasedOn() {
    return this.fields?.['LOCK_EMPLOYEE_STATUS_BASED_ON']?.values?.[0] || '';
  }

  get attachmentListItems() {
    return {
      programApproval: this.fields?.['PROGRAM_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD']?.values || [],
      coursePreApproval: this.fields?.['COURSE_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD']?.values || [],
      courseCompletion: this.fields?.['COMPLETION_DOCUMENTS_ATTACHMENT_FIELD']?.values || [],
    };
  }

  get attachmentInstructions() {
    return {
      programApproval: this.fields?.['PROGRAM_APPROVAL_DOCUMENTS_STRING']?.values?.[0] || '',
      coursePreApproval: this.fields?.['COURSE_APPROVAL_DOCUMENTS_STRING']?.values?.[0] || '',
      courseCompletion: this.fields?.['COMPLETION_DOCUMENTS_STRING']?.values?.[0] || '',
    };
  }

  get expenseType() {
    return this.fields?.['EXPENSE_TYPE']?.values?.[0] || '';
  }
}

const Registration = {
  kind: 'object',
  name: 'tas-program-template',
  features: TasProgramTemplateExtension,
};
export default Registration;
